<template>
  <div ref="rootRef" class="global-search" :class="{ 'global-search--expanded': isOpen, 'global-search--mobile': isMobile }">
    <div class="global-search__trigger" @click="openSearch">
      <VaIcon name="search" class="global-search__trigger-icon" size="18px" />
      <span v-if="!isMobile" class="global-search__trigger-label">Search drivers, assets, devices…</span>
      <kbd v-if="!isMobile" class="global-search__kbd">Ctrl K</kbd>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="global-search-overlay"
        @click.self="closeSearch"
      >
        <div class="global-search-panel" role="dialog" aria-label="Global search">
          <div class="global-search-panel__header">
            <VaIcon name="search" class="global-search-panel__search-icon" size="20px" />
            <input
              ref="inputRef"
              v-model="query"
              type="search"
              class="global-search-panel__input"
              placeholder="Search by name, ID, phone, plate, VIN, telemetry ID…"
              autocomplete="off"
              @keydown="onKeydown"
            />
            <button type="button" class="global-search-panel__close" aria-label="Close search" @click="closeSearch">
              <VaIcon name="close" size="18px" />
            </button>
          </div>

          <div class="global-search-panel__body">
            <div v-if="loading" class="global-search-panel__status">
              <VaProgressCircle indeterminate size="small" />
              <span>Searching…</span>
            </div>

            <div v-else-if="query.trim().length < 2" class="global-search-panel__status global-search-panel__hint">
              Type at least 2 characters to search across drivers, assets, tracking devices, clients, and more.
            </div>

            <div v-else-if="!groups.length && !loading" class="global-search-panel__status">
              No results for “{{ query.trim() }}”.
            </div>

            <div v-else class="global-search-panel__groups">
              <section v-for="group in groups" :key="group.type" class="global-search-group">
                <header class="global-search-group__header">
                  <VaIcon :name="group.icon" size="16px" />
                  <span>{{ group.label }}</span>
                  <span class="global-search-group__count">{{ group.results.length }}</span>
                </header>
                <ul class="global-search-group__list">
                  <li
                    v-for="(item, idx) in group.results"
                    :key="item.id"
                    class="global-search-item"
                    :class="{ 'global-search-item--active': flatIndex(group, idx) === activeIndex }"
                    @mouseenter="activeIndex = flatIndex(group, idx)"
                    @click="goTo(item)"
                  >
                    <div class="global-search-item__main">
                      <span class="global-search-item__title">{{ item.title }}</span>
                      <span v-if="item.meta" class="global-search-item__meta">{{ item.meta }}</span>
                    </div>
                    <span v-if="item.subtitle" class="global-search-item__subtitle">{{ item.subtitle }}</span>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { runGlobalSearch } from '@/services/globalSearchService'
import type { GlobalSearchGroup, GlobalSearchResult } from '@/types/globalSearch'

const props = defineProps({
  isMobile: { type: Boolean, default: false },
})

const router = useRouter()
const rootRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const query = ref('')
const groups = ref<GlobalSearchGroup[]>([])
const loading = ref(false)
const activeIndex = ref(0)

const flatResults = computed(() => {
  const out: GlobalSearchResult[] = []
  for (const group of groups.value) {
    out.push(...group.results)
  }
  return out
})

function flatIndex(group: GlobalSearchGroup, idx: number): number {
  let offset = 0
  for (const g of groups.value) {
    if (g.type === group.type) return offset + idx
    offset += g.results.length
  }
  return 0
}

const debouncedSearch = useDebounceFn(async (value: string) => {
  const trimmed = value.trim()
  if (trimmed.length < 2) {
    groups.value = []
    loading.value = false
    return
  }
  loading.value = true
  try {
    groups.value = await runGlobalSearch(trimmed)
    activeIndex.value = 0
  } finally {
    loading.value = false
  }
}, 300)

watch(query, (value) => {
  if (!isOpen.value) return
  loading.value = value.trim().length >= 2
  debouncedSearch(value)
})

function openSearch() {
  isOpen.value = true
  nextTick(() => inputRef.value?.focus())
}

function closeSearch() {
  isOpen.value = false
  query.value = ''
  groups.value = []
  activeIndex.value = 0
}

function goTo(item: GlobalSearchResult) {
  closeSearch()
  router.push(item.route)
}

function onKeydown(event: KeyboardEvent) {
  const total = flatResults.value.length
  if (event.key === 'Escape') {
    event.preventDefault()
    closeSearch()
    return
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!total) return
    activeIndex.value = (activeIndex.value + 1) % total
    return
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (!total) return
    activeIndex.value = (activeIndex.value - 1 + total) % total
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    const item = flatResults.value[activeIndex.value]
    if (item) goTo(item)
  }
}

function onGlobalKeydown(event: KeyboardEvent) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    if (isOpen.value) {
      closeSearch()
    } else {
      openSearch()
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', onGlobalKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
})
</script>

<style lang="scss" scoped>
.global-search {
  flex: 1;
  max-width: 28rem;
  min-width: 0;

  &--mobile {
    max-width: none;
    flex: 0;
  }
}

.global-search__trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.45rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
  }
}

.global-search--mobile .global-search__trigger {
  width: auto;
  padding: 0.45rem;
}

.global-search__trigger-icon {
  color: rgba(255, 255, 255, 0.75);
  flex-shrink: 0;
}

.global-search__trigger-label {
  flex: 1;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.global-search__kbd {
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.6);
  font-family: inherit;
}

.global-search-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(5, 20, 54, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4.5rem 1rem 1rem;
}

.global-search-panel {
  width: min(720px, 100%);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.global-search-panel__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e8edf3;
}

.global-search-panel__search-icon {
  color: #64748b;
  flex-shrink: 0;
}

.global-search-panel__input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: #0f172a;
  background: transparent;

  &::placeholder {
    color: #94a3b8;
  }
}

.global-search-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
}

.global-search-panel__body {
  max-height: min(60vh, 520px);
  overflow-y: auto;
}

.global-search-panel__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: #64748b;
  font-size: 0.875rem;
}

.global-search-panel__hint {
  text-align: center;
  line-height: 1.5;
}

.global-search-panel__groups {
  padding: 0.5rem 0;
}

.global-search-group__header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.global-search-group__count {
  margin-left: auto;
  font-weight: 600;
  color: #94a3b8;
}

.global-search-group__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.global-search-item {
  padding: 0.65rem 1rem;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: background 0.1s ease;

  &:hover,
  &--active {
    background: #f8fafc;
    border-left-color: var(--va-primary);
  }
}

.global-search-item__main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.global-search-item__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.global-search-item__meta {
  font-size: 0.7rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  flex-shrink: 0;
}

.global-search-item__subtitle {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
